import { RefObject, useEffect, useState } from 'react';

/**
 * Shared IntersectionObserver registry, keyed by rootMargin.
 *
 * A prod lobby can render 100+ cash tiles plus a few hundred tournament tiles
 * (8/day across a multi-week window). Giving every tile its own observer means
 * hundreds of live IntersectionObserver instances; instead we observe all
 * elements that share a rootMargin with a SINGLE observer and fan the entries
 * back out to per-element callbacks. The observer is torn down once its last
 * subscriber unmounts so nothing leaks between lobby mounts.
 */
type VisibilityCallback = (visible: boolean) => void;

interface SharedObserver {
  observer: IntersectionObserver;
  callbacks: Map<Element, VisibilityCallback>;
}

const registry = new Map<string, SharedObserver>();

function getSharedObserver(rootMargin: string): SharedObserver {
  let shared = registry.get(rootMargin);
  if (!shared) {
    const callbacks = new Map<Element, VisibilityCallback>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          callbacks.get(entry.target)?.(entry.isIntersecting);
        }
      },
      { rootMargin }
    );
    shared = { observer, callbacks };
    registry.set(rootMargin, shared);
  }
  return shared;
}

/**
 * Track whether an element is currently within (or near) the viewport.
 * Useful for gating expensive per-element animations on visibility — avoids
 * running infinite CSS/GSAP loops on cards scrolled off-screen.
 *
 * Backed by a shared observer (see above) so observing many elements stays
 * cheap at lobby scale. `rootMargin` defaults to 100px so animations start a
 * bit before the element actually scrolls into view, hiding the wind-up.
 */
export function useInViewport<T extends Element>(
  ref: RefObject<T>,
  rootMargin = '100px'
): boolean {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback for environments without IO — assume visible.
      setIsVisible(true);
      return;
    }
    const shared = getSharedObserver(rootMargin);
    shared.callbacks.set(el, setIsVisible);
    shared.observer.observe(el);
    return () => {
      shared.observer.unobserve(el);
      shared.callbacks.delete(el);
      // Drop the observer entirely once nothing is using it.
      if (shared.callbacks.size === 0) {
        shared.observer.disconnect();
        registry.delete(rootMargin);
      }
    };
  }, [ref, rootMargin]);

  return isVisible;
}
